import {
	Button,
	Input,
	Label,
	Modal,
	ModalContent,
	ModalDescription,
	ModalHeader,
	NativeSelect,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
	Textarea,
} from "@ondit/ui";
import { useState } from "react";
import { toast } from "sonner";
import { Async, PageHead, useConfirm } from "@/components/ui";
import { useI18n, useT } from "@/i18n";
import { ApiError, api, type ModelCall } from "@/lib/api";
import { useAsync } from "@/lib/hooks";

export function ModelCallsPage() {
	const t = useT();
	const { locale } = useI18n();
	const confirm = useConfirm();
	const [offset, setOffset] = useState(0);
	const state = useAsync(() => api.modelCalls(offset), [offset]);
	const [selected, setSelected] = useState<ModelCall | null>(null);
	const [detail, setDetail] = useState<unknown>(null);
	const [reference, setReference] = useState("");
	const [note, setNote] = useState("");
	const [decision, setDecision] = useState("receipt");
	const [receipt, setReceipt] = useState("");
	const [busy, setBusy] = useState(false);
	const error = (e: unknown) => toast.error(explain(e));
	// A receipt that is not JSON is the operator's typo, and the parser's
	// sentence says where it is; "not accepted" would send them hunting.
	const explain = (e: unknown) =>
		e instanceof ApiError
			? e.localized(locale)
			: e instanceof SyntaxError
				? t("errors.invalid_json", { detail: e.message })
				: t("errors.bad_request");
	async function inspect(row: ModelCall) {
		setBusy(true);
		try {
			const result = await api.modelCall(row.id);
			setSelected(row);
			setDetail(result);
			setReference("");
			setNote("");
			setReceipt("");
			setDecision("receipt");
		} catch (e) {
			error(e);
		} finally {
			setBusy(false);
		}
	}
	async function submit() {
		if (!selected) return;
		let message: unknown;
		try {
			message = decision === "receipt" ? JSON.parse(receipt) : undefined;
		} catch (e) {
			error(e);
			return;
		}
		const body = {
			request_id: selected.request_id,
			reference,
			note,
			decision: decision === "receipt" ? { type: "receipt", message } : { type: "not_billed" },
		};
		setBusy(true);
		try {
			if (
				!(await confirm(
					t("reconcile.apply"),
					t(decision === "receipt" ? "reconcile.chargeConsequence" : "reconcile.releaseConsequence"),
				))
			)
				return;
			await api.reconcileModelCall(selected.id, body);
			setSelected(null);
			state.reload();
		} catch (e) {
			error(e);
		} finally {
			setBusy(false);
		}
	}
	return (
		<>
			<PageHead title={t("reconcile.title")} />
			<div className="table-tools">
				<Button variant="default" onClick={state.reload}>
					{t("reconcile.refresh")}
				</Button>
			</div>
			<Async state={state}>
				{({ calls, total }) => (
					<>
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>{t("reconcile.call")}</TableHead>
									<TableHead>{t("reconcile.brand")}</TableHead>
									<TableHead>{t("reconcile.status")}</TableHead>
									<TableHead>{t("reconcile.reserved")}</TableHead>
									<TableHead />
								</TableRow>
							</TableHeader>
							<TableBody>
								{calls.map((row) => (
									<TableRow key={row.id}>
										<TableCell>
											{row.model}
											<br />
											{row.request_id}
										</TableCell>
										<TableCell>
											{row.org_id}
											<br />
											{row.entity_id}
										</TableCell>
										<TableCell>{t(`reconcile.${row.status}`)}</TableCell>
										<TableCell>{row.reservation}</TableCell>
										<TableCell>
											<Button size="sm" variant="default" disabled={busy} onClick={() => void inspect(row)}>
												{t("reconcile.inspect")}
											</Button>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
						{total === 0 && <p>{t("reconcile.empty")}</p>}
						<div className="table-tools">
							<Button variant="default" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - 50))}>
								{t("reconcile.previous")}
							</Button>
							<span>
								{Math.min(offset + calls.length, total)} / {total}
							</span>
							<Button
								variant="default"
								disabled={offset + calls.length >= total}
								onClick={() => setOffset(offset + 50)}
							>
								{t("reconcile.next")}
							</Button>
						</div>
					</>
				)}
			</Async>
			<Modal
				open={selected !== null}
				onOpenChange={(open) => {
					if (!open && !busy) setSelected(null);
				}}
			>
				<ModalContent>
					<ModalHeader closeLabel={t("reconcile.close")}>{t("reconcile.inspect")}</ModalHeader>
					<ModalDescription>{t("reconcile.evidenceRule")}</ModalDescription>
					<form
						onSubmit={(e) => {
							e.preventDefault();
							void submit();
						}}
					>
						<fieldset className="stack" disabled={busy}>
							<Label htmlFor="reconcile-original">{t("reconcile.original")}</Label>
							<Textarea id="reconcile-original" readOnly rows={6} value={JSON.stringify(detail, null, 2)} />
							<Label htmlFor="reconcile-reference">{t("reconcile.reference")}</Label>
							<Input
								id="reconcile-reference"
								required
								maxLength={2000}
								value={reference}
								onChange={(e) => setReference(e.target.value)}
							/>
							<Label htmlFor="reconcile-note">{t("reconcile.note")}</Label>
							<Textarea
								id="reconcile-note"
								required
								maxLength={8000}
								rows={2}
								value={note}
								onChange={(e) => setNote(e.target.value)}
							/>
							<Label htmlFor="reconcile-decision">{t("reconcile.decision")}</Label>
							<NativeSelect id="reconcile-decision" value={decision} onChange={(e) => setDecision(e.target.value)}>
								<option value="receipt">{t("reconcile.receipt")}</option>
								<option value="not_billed">{t("reconcile.notBilled")}</option>
							</NativeSelect>
							{decision === "receipt" && (
								<>
									<Label htmlFor="reconcile-receipt">{t("reconcile.receiptJson")}</Label>
									<Textarea
										id="reconcile-receipt"
										required
										rows={6}
										value={receipt}
										onChange={(e) => setReceipt(e.target.value)}
									/>
									<p>{t("reconcile.receiptShape")}</p>
								</>
							)}
							<p>{t(decision === "receipt" ? "reconcile.chargeConsequence" : "reconcile.releaseConsequence")}</p>
							<Button type="submit">{t("reconcile.apply")}</Button>
						</fieldset>
					</form>
				</ModalContent>
			</Modal>
		</>
	);
}
